import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import ImageHeader from '../../elements/ImageHeader';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import ErrorBoundary from '../../utils/ErrorBoundary';
import FareItemCallToAction from '../_commons/atoms/FareItemCallToAction';

const CarCard = (props) => {
  const {
    focusReferences,
    tabIndex,
    tabIndexCallToAction,
    usedErrorBoundary,
    imageHeader,
    carItemBody,
    carModelDescriptionContainer,
    carModel,
    carDescription,
    carItemPrice,
    showCallToAction,
    fareItemCallToAction,
    carItemFooter,
    data,
    showPill,
    carItemPill,
    deepLinkCampaign
  } = props;
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const { model, description, image, navigationInfo, deeplink } = data;
  const pillAvailable = showPill && carItemPill && data && navigationInfo;

  const doRedirect = () => {
    if (isSSR()) {
      return;
    } else {
      window.open(`${deeplink}${deepLinkCampaign}`, '_top');
    }
  };

  const cardRender = (
    <Box
      display="flex"
      flexDirection="column"
      focusReferences={focusReferences}
      tabIndex={tabIndex ? 0 : null}
      focusable={tabIndex}
      className={className}
      style={style}
    >
      {pillAvailable && (
        <Box display="flex" justifyContent="end">
          <Text {...carItemPill}>{data.navigationInfo}</Text>
        </Box>
      )}
      <ImageHeader {...imageHeader} image={image} />
      <Box display="flex" justifyContent="between" {...carItemBody}>
        <Box display="flex" flexDirection="column" {...carModelDescriptionContainer}>
          <Text {...carModel}>{model}</Text>
          <Text {...carDescription}>{description}</Text>
        </Box>
        <FareItemPrice {...carItemPrice} {...data} />
      </Box>
      {showCallToAction && (
        <Box {...carItemFooter}>
          <FareItemCallToAction
            tabIndex={tabIndexCallToAction}
            {...fareItemCallToAction}
            onClick={doRedirect}
          />
        </Box>
      )}
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{cardRender}</ErrorBoundary> : { cardRender };
};

CarCard.propTypes = {
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Define if the dayCard will have focus for Screen Readers
   */
  tabIndex: PropTypes.bool,
  /**
   * Function to handle focus reference of the component
   */
  focusReferences: PropTypes.func,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  tabIndexCallToAction: PropTypes.bool,
  /**
   * ImageHeaderProps
   */
  imageHeader: PropTypes.objectOf(PropTypes.any),
  /**
   * carItemBody props
   */
  carItemBody: PropTypes.objectOf(PropTypes.any),
  /**
   * carItemPrice props
   */
  carItemPrice: PropTypes.objectOf(PropTypes.any),
  /**
   * carItemPill props
   */
  carItemPill: PropTypes.objectOf(PropTypes.any),
  /**
   * carModelDescriptionContainer props
   */
  carModelDescriptionContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * carModel props
   */
  carModel: PropTypes.objectOf(PropTypes.any),
  /**
   * carDescription props
   */
  carDescription: PropTypes.objectOf(PropTypes.any),
  /**
   * showCallToAction
   */
  showCallToAction: PropTypes.bool,
  /**
   * showPill
   */
  showPill: PropTypes.bool,
  /**
   * fareItemCallToAction props
   */
  fareItemCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * carItemFooter props
   */
  carItemFooter: PropTypes.objectOf(PropTypes.any),
  /**
   * Append text to campaign deeplink
   */
  deepLinkCampaign: PropTypes.string
};

CarCard.defaultProps = {
  data: null,
  usedErrorBoundary: true,
  tabIndex: true,
  tabIndexCallToAction: true,
  focusReferences: () => {},
  imageHeader: {},
  carItemBody: {},
  carItemPrice: {},
  carItemPill: {},
  carModelDescriptionContainer: {},
  carModel: {},
  carDescription: {},
  showCallToAction: true,
  showPill: false,
  fareItemCallToAction: {},
  carItemFooter: {},
  deepLinkCampaign: '&utm_campaign=TRFX'
};

CarCard.componentName = 'CarCard';

export default CarCard;
